import { useEffect, useId, useRef, useState } from 'react';
import i18n from '@/locales/init';
import { MenuSvg } from '../../Svg';
import { ExamColour, ExamFont, useExamDisplay } from '.';
import './settings.less';

const fonts: ExamFont[] = ['standard', 'large', 'extra-large'];
const colours: ExamColour[] = ['standard', 'sepia', 'dark'];

export default function DisplaySettings() {
  const display = useExamDisplay();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const id = useId();

  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    const selected = panel?.querySelector<HTMLInputElement>('input:checked');
    (selected || panel)?.focus();
    const closeOnOutside = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', closeOnOutside);
    return () => document.removeEventListener('pointerdown', closeOnOutside);
  }, [open]);

  if (!display) return null;
  const label = i18n.t('common.dauyan_exam_display_settings');
  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  return <div className="exam-display-settings" ref={rootRef}
    onKeyDown={event => {
      if (event.key === 'Escape' && open) {
        event.stopPropagation();
        close();
      }
    }}
    onBlur={event => {
      if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget as Node)) setOpen(false);
    }}>
    <button type="button" className="exam-display-settings-trigger" ref={triggerRef}
      aria-label={label} title={label} aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? id : undefined}
      onClick={() => setOpen(current => !current)}>{MenuSvg}</button>
    {open && <div id={id} ref={panelRef} className="exam-display-settings-panel" role="dialog" aria-label={label} tabIndex={-1}>
      <div className="exam-display-settings-heading">
        <strong>{label}</strong>
        <button type="button" onClick={close} aria-label={i18n.t('common.dauyan_exam_display_close')}>×</button>
      </div>
      <fieldset>
        <legend>{i18n.t('common.dauyan_exam_font_size')}</legend>
        {fonts.map(font => <label key={font}>
          <input type="radio" name={`${id}-font`} value={font} checked={display.preferences.font === font}
            onChange={() => display.setFont(font)} />
          <span>{i18n.t(`common.dauyan_exam_font_${font.replace('-', '_')}`)}</span>
        </label>)}
      </fieldset>
      <fieldset>
        <legend>{i18n.t('common.dauyan_exam_screen_colour')}</legend>
        {colours.map(colour => <label key={colour}>
          <input type="radio" name={`${id}-colour`} value={colour} checked={display.preferences.colour === colour}
            onChange={() => display.setColour(colour)} />
          <span className="exam-display-settings-swatch" data-colour={colour} aria-hidden="true" />
          <span>{i18n.t(`common.dauyan_exam_colour_${colour}`)}</span>
        </label>)}
      </fieldset>
      <button type="button" className="exam-display-settings-reset" onClick={display.reset}>
        {i18n.t('common.dauyan_exam_display_reset')}
      </button>
    </div>}
  </div>;
}
